import { getCanPromote } from "../../backoffice/dtos/authentication/user";
import { Promotion } from "../../backoffice/dtos/promotions/promotion";
import { user } from "../../backoffice/dtos/authentication/user";
import { Dispatch, SetStateAction } from "react";

export class Item {
    description: string;
    itemDescription: string;
    item: Item[] | undefined;
    promotions: Promotion[] | undefined;

    constructor(description: string, itemDescription: string, item: Item[] | undefined, promotions: Promotion[] | undefined) {
        if (item === undefined && promotions === undefined)
            throw new Error("Either item or promotions must be defined");

        if (item !== undefined && promotions !== undefined)
            throw new Error("One of item and promotions must be defined");

        this.description = description;
        this.itemDescription = itemDescription;
        this.item = item;
        this.promotions = promotions;
    }

    isItem(): boolean {
        return this.item !== undefined;
    }

    isPromotions(): boolean {
        return this.promotions !== undefined;
    }

    canPromote(user: user | undefined): boolean {
        if (this.item !== undefined) {
            return this.item.some(m => m.canPromote(user));
        }

        return this.promotions !== undefined
            && user !== undefined
                && this.promotions.some(m => getCanPromote(user, m));
    }

    getApproveSelected(user: user | undefined, selectedElements: Map<number, { promotion: Promotion, approve: boolean }>): boolean | null {
        if (user === undefined) {
            return null;
        }

        if (this.promotions !== undefined) {
            if (this.promotions.length === 0) {
                return null;
            }

            return this.promotions.every(
                p => !getCanPromote(user, p) || (selectedElements.get(p.id)?.approve === true)
            );
        }

        for(const item of this.item!) {
            if (!item.getApproveSelected(user, selectedElements)) {
                return false;
            }
        }
        return true;
    }


    getRejectSelected(user: user | undefined, selectedElements: Map<number, { promotion: Promotion, approve: boolean }>): boolean | null {
        if (user === undefined) {
            return null;
        }

        if (this.promotions !== undefined) {
            if (this.promotions.length === 0) {
                return null;
            }

            return this.promotions.every(
                p => !getCanPromote(user, p) || (selectedElements.get(p.id)?.approve === false)
            );
        }

        for(const item of this.item!) {
            if (!item.getRejectSelected(user, selectedElements)) {
                return false;
            }
        }
        return true;
    }

    approvalFlow(
        user: user | undefined,
        selectedElements: Map<number, { promotion: Promotion, approve: boolean }>,
        setSelectedElements: Dispatch<SetStateAction<Map<number, { promotion: Promotion, approve: boolean }>>>
    ) {
        if (!user)
            return

        const approveSelected = this.getApproveSelected(
            user,
            selectedElements
        );

        for (const promotion of this) {
            if (!getCanPromote(user, promotion)) {
                continue;
            }

            if (!approveSelected) {
                selectedElements.set(promotion.id, {
                    approve: true,
                    promotion: promotion
                });
            } else {
                selectedElements.delete(promotion.id);
            }
        }
        const newElements = new Map(selectedElements);
        setSelectedElements(newElements);
    }

    rejectFlow(
        user: user | undefined,
        selectedElements: Map<number, { promotion: Promotion, approve: boolean }>,
        setSelectedElements: Dispatch<SetStateAction<Map<number, { promotion: Promotion, approve: boolean }>>>
    ) {
        if (!user)
            return

        const rejectSelected = this.getRejectSelected(
            user,
            selectedElements
        );

        for (const promotion of this) {
            if (!getCanPromote(user, promotion)) {
                continue;
            }

            if (!rejectSelected) {
                selectedElements.set(promotion.id, {
                    approve: false,
                    promotion: promotion
                });
            } else {
                selectedElements.delete(promotion.id);
            }
        }
        const newElements = new Map(selectedElements);
        setSelectedElements(newElements);
    }

    getChild(): Item[] | Promotion[] {
        if (this.item !== undefined)
        {
            return this.item;
        }

        return this.promotions!;
    }

    [Symbol.iterator](): Iterator<Promotion> {
        if (this.isPromotions()){
            return this.promotions![Symbol.iterator]();
        }

        return iterate(this.item!);
    }
}

function* iterate(items: Item[]): Generator<Promotion> {
    for (const item of items) {
        for (const promotion of item) {
            yield promotion;
        }
    }
}

export function getItemByItem(description: string, itemDescription: string, item: Item[]): Item {
    return new Item(description, itemDescription, item, undefined);
}

export function getItemByPromotions(description: string, itemDescription: string, promotions: Promotion[]): Item {
    return new Item(description, itemDescription, undefined, promotions);
}
